import React, { useState, useEffect } from 'react'
import { Modal, Button, Typography, Space } from 'antd'
import { ExclamationCircleOutlined, LinkOutlined } from '@ant-design/icons'
import { eventManager } from 'utils'
import type { PasswordReminderState, PasswordReminderEventData, PasswordReminderCallbacks } from './PasswordReminderModal.types'
import {
  ModalContent,
  MessageText,
  RequirementsList,
  ExternalLinkSection,
  ExternalLinkText,
  ExternalUrl,
  IressStatusSection,
  IressStatusText,
  SupportText,
  ActionButtonsContainer,
} from './PasswordReminderModal.styles'

const { Title } = Typography

const initialState: PasswordReminderState = {
  isOpen: false,
  loading: false,
  data: undefined,
  callbacks: undefined,
}

const PasswordReminderModal: React.FC = () => {
  const [modalState, setModalState] = useState<PasswordReminderState>(initialState)

  useEffect(() => {
    const handleModalEvent = (data?: PasswordReminderEventData, cb?: PasswordReminderCallbacks) => {
      setModalState((state) => ({
        ...state,
        isOpen: data !== undefined,
        data,
        callbacks: cb,
        loading: false, // Reset loading state when modal opens
      }))
    }

    eventManager.addListener('password-reminder-modal', handleModalEvent)

    // Cleanup function
    return () => {
      // Note: eventManager doesn't have removeListener, so we rely on component unmount
    }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGoToUpdate = () => {
    if (modalState.callbacks?.onGoToUpdate) {
      modalState.callbacks.onGoToUpdate()
    }
  }

  const handleRemindLater = () => {
    if (modalState.callbacks?.onRemindLater) {
      modalState.callbacks.onRemindLater()
    }
  }

  const { isOpen, loading, data } = modalState

  if (!data) {
    return null
  }

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          {data.title}
        </Space>
      }
      open={isOpen}
      onCancel={handleRemindLater}
      width={600}
      closable={false}
      maskClosable={false}
      footer={
        <ActionButtonsContainer>
          <Button onClick={handleRemindLater} disabled={loading}>
            Remind Me Later
          </Button>
          <Button type="primary" icon={<LinkOutlined />} onClick={handleGoToUpdate} disabled={loading}>
            Go to Update Password
          </Button>
        </ActionButtonsContainer>
      }
    >
      <ModalContent>
        <MessageText>{data.message}</MessageText>

        <Title level={5}>New Password Requirements:</Title>
        <RequirementsList>
          {data.passwordRequirements.map((requirement, index) => (
            <li key={index}>{requirement}</li>
          ))}
        </RequirementsList>

        <ExternalLinkSection>
          <ExternalLinkText>
            <strong>Update your password at:</strong>
          </ExternalLinkText>
          <ExternalUrl href={data.externalUrl} target="_blank" rel="noopener noreferrer">
            {data.externalUrl}
          </ExternalUrl>
        </ExternalLinkSection>

        <IressStatusSection>
          <IressStatusText>
            <strong>Important:</strong> After updating your password on their platform, On our platform the "IRESS Status (Section in header)" is verified with old credentials and needs to be updated with your new credentials.
          </IressStatusText>
        </IressStatusSection>

        <SupportText>
          If you have any issues, contact: <a href={`mailto:${data.supportEmail}`}>{data.supportEmail}</a>
        </SupportText>
      </ModalContent>
    </Modal>
  )
}

export default PasswordReminderModal
